import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Compass, LogOut, RefreshCw, ArrowLeft, Mail, KeyRound, CheckCircle2, Undo2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail) => {
  if (detail == null) return "Noe gikk galt. Prøv igjen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const fmtDate = (iso) => {
  try {
    return new Intl.DateTimeFormat("nb-NO", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const resetToken = searchParams.get("reset");
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [tab, setTab] = useState("forslag");
  const [suggestions, setSuggestions] = useState([]);
  const [stats, setStats] = useState(null);

  const loadInquiries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/inquiries`, { withCredentials: true });
      setInquiries(data.inquiries || []);
    } catch (e) {
      if (e.response?.status === 401) setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminData = async () => {
    try {
      const [g, s] = await Promise.all([
        axios.get(`${API}/admin/suggestions`, { withCredentials: true }),
        axios.get(`${API}/partner/stats`, { withCredentials: true }),
      ]);
      setSuggestions(g.data.suggestions || []);
      setStats(s.data);
    } catch {}
  };

  const reviewSuggestion = async (id, status) => {
    setSuggestions((arr) => arr.map((s) => (s.id === id ? { ...s, status } : s)));
    try {
      await axios.patch(`${API}/admin/suggestions/${id}`, { status }, { withCredentials: true });
      toast.success(status === "godkjent" ? "Forslaget er publisert i ressurskatalogen." : "Forslaget er avvist.");
    } catch {
      toast.error("Kunne ikke oppdatere forslaget.");
      loadAdminData();
    }
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data?.role === "admin" ? res.data : false))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) {
      loadInquiries();
      loadAdminData();
    }
  }, [user]);

  const maxDay = stats?.days?.length ? Math.max(...stats.days.map((d) => d.count), 1) : 1;
  const pendingCount = suggestions.filter((s) => s.status === "venter").length;

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const forgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email }, { withCredentials: true });
      setInfo("Hvis e-postadressen er registrert, er en tilbakestillingslenke sendt. Sjekk innboksen din.");
      toast.success("Tilbakestillingslenke sendt hvis e-posten er registrert.");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("Passordene er ikke like.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
      toast.success("Passordet er endret! Logg inn med det nye passordet.");
      setSearchParams({});
      setMode("login");
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (iq) => {
    const next = iq.status === "behandlet" ? "ny" : "behandlet";
    setInquiries((arr) => arr.map((x) => (x.id === iq.id ? { ...x, status: next } : x)));
    try {
      await axios.patch(`${API}/admin/inquiries/${iq.id}`, { status: next }, { withCredentials: true });
    } catch {
      setInquiries((arr) => arr.map((x) => (x.id === iq.id ? { ...x, status: iq.status } : x)));
      toast.error("Kunne ikke oppdatere status.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    setInquiries([]);
  };

  const newCount = inquiries.filter((iq) => iq.status !== "behandlet").length;
  const sorted = [...inquiries].sort((a, b) => (a.status === "behandlet") - (b.status === "behandlet"));

  return (
    <div className="min-h-screen bg-background text-foreground antialiased">
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-back-link" className="flex items-center gap-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              <Compass size={16} />
            </span>
            <span className="font-serif font-bold text-lg text-foreground">Gjeldsveiviseren</span>
            <span className="hidden sm:inline-flex items-center gap-1 text-xs"><ArrowLeft size={12} /> Til forsiden</span>
          </Link>
          {user && (
            <button data-testid="admin-logout-btn" onClick={logout}
              className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
              <LogOut size={14} /> Logg ut
            </button>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        {checking ? (
          <p className="text-sm text-muted-foreground" data-testid="admin-loading">Laster …</p>
        ) : !user ? (
          <div className="max-w-md mx-auto bg-surface border border-border rounded-3xl p-8 lg:p-10 mt-8">
            {mode === "reset" ? (
              <>
                <h1 className="font-serif font-bold text-2xl flex items-center gap-2.5"><KeyRound size={20} className="text-primary" /> Velg nytt passord</h1>
                <p className="mt-2 text-sm text-muted-foreground">Skriv inn et nytt passord for admin-kontoen.</p>
                <form onSubmit={resetPassword} className="mt-6 space-y-4">
                  <label className="block">
                    <span className="text-xs font-semibold text-muted-foreground">Nytt passord (minst 8 tegn)</span>
                    <input data-testid="admin-reset-password" type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-muted-foreground">Gjenta passordet</span>
                    <input data-testid="admin-reset-confirm" type="password" required minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200" />
                  </label>
                  {error && <p data-testid="admin-login-error" className="text-sm text-destructive">{error}</p>}
                  <button data-testid="admin-reset-submit" type="submit" disabled={loading}
                    className="w-full bg-primary text-primary-foreground px-6 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 disabled:opacity-60">
                    {loading ? "Lagrer …" : "Lagre nytt passord"}
                  </button>
                </form>
              </>
            ) : mode === "forgot" ? (
              <>
                <h1 className="font-serif font-bold text-2xl flex items-center gap-2.5"><KeyRound size={20} className="text-primary" /> Glemt passord</h1>
                <p className="mt-2 text-sm text-muted-foreground">Skriv inn e-postadressen din, så sender vi en tilbakestillingslenke.</p>
                {info ? (
                  <p data-testid="admin-forgot-success" className="mt-6 text-sm bg-accent text-accent-foreground rounded-xl px-4 py-3.5">{info}</p>
                ) : (
                  <form onSubmit={forgot} className="mt-6 space-y-4">
                    <label className="block">
                      <span className="text-xs font-semibold text-muted-foreground">E-post</span>
                      <input data-testid="admin-forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200"
                        placeholder="din@epost.no" />
                    </label>
                    {error && <p data-testid="admin-login-error" className="text-sm text-destructive">{error}</p>}
                    <button data-testid="admin-forgot-submit" type="submit" disabled={loading}
                      className="w-full bg-primary text-primary-foreground px-6 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 disabled:opacity-60">
                      {loading ? "Sender …" : "Send tilbakestillingslenke"}
                    </button>
                  </form>
                )}
                <button data-testid="admin-back-to-login" onClick={() => { setMode("login"); setInfo(""); setError(""); }}
                  className="mt-5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
                  ← Tilbake til innlogging
                </button>
              </>
            ) : (
              <>
                <h1 className="font-serif font-bold text-2xl">Admin-innlogging</h1>
                <p className="mt-2 text-sm text-muted-foreground">Logg inn for å lese innsendte henvendelser.</p>
                <form onSubmit={login} className="mt-6 space-y-4">
                  <label className="block">
                    <span className="text-xs font-semibold text-muted-foreground">E-post</span>
                    <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200"
                      placeholder="din@epost.no" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-muted-foreground">Passord</span>
                    <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                      className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200"
                      placeholder="••••••••" />
                  </label>
                  {error && <p data-testid="admin-login-error" className="text-sm text-destructive">{error}</p>}
                  <button data-testid="admin-login-submit" type="submit" disabled={loading}
                    className="w-full bg-primary text-primary-foreground px-6 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 disabled:opacity-60">
                    {loading ? "Logger inn …" : "Logg inn"}
                  </button>
                  <button type="button" data-testid="admin-forgot-link" onClick={() => { setMode("forgot"); setError(""); }}
                    className="w-full text-center text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
                    Glemt passord?
                  </button>
                </form>
              </>
            )}
          </div>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-terracotta">Admin</p>
                <h1 className="mt-2 font-serif font-bold tracking-tight text-2xl sm:text-3xl text-primary">Kontrollpanel</h1>
                <p className="mt-2 text-sm text-muted-foreground">Innlogget som {user.email}</p>
              </div>
              <button data-testid="admin-refresh-btn" onClick={() => { loadInquiries(); loadAdminData(); }} disabled={loading}
                className="inline-flex items-center gap-2 border border-foreground/20 px-5 py-2.5 rounded-full text-xs font-semibold transition-all duration-200 hover:border-foreground disabled:opacity-50">
                <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Oppdater
              </button>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {[
                { id: "forslag", label: `Forslag fra NAV${pendingCount ? ` (${pendingCount} nye)` : ""}`, testid: "admin-tab-suggestions" },
                { id: "statistikk", label: "Statistikk", testid: "admin-tab-stats" },
                { id: "henvendelser", label: "Henvendelser", testid: "admin-tab-inquiries" },
              ].map((t) => (
                <button key={t.id} data-testid={t.testid} onClick={() => setTab(t.id)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${
                    tab === t.id ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground hover:bg-primary/15"
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "forslag" && (
              <div className="mt-6 space-y-4" data-testid="admin-suggestions-list">
                {suggestions.map((s) => (
                  <article key={s.id} data-testid="admin-suggestion-row" className="bg-surface border border-border rounded-2xl p-6">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-[11px] font-mono font-semibold uppercase tracking-[0.15em] bg-accent text-accent-foreground px-3 py-1.5 rounded-full">
                        {s.category}
                      </span>
                      <span className={`text-[11px] font-mono font-semibold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full ${
                        s.status === "godkjent" ? "bg-accent text-accent-foreground" : s.status === "avvist" ? "bg-terracotta/10 text-terracotta" : "bg-muted text-muted-foreground"
                      }`}>
                        {s.status === "godkjent" ? "Godkjent" : s.status === "avvist" ? "Avvist" : "Venter"}
                      </span>
                      <span className="text-xs font-mono text-muted-foreground">{fmtDate(s.created_at)} · fra {s.by}</span>
                      {s.status === "venter" && (
                        <span className="ml-auto flex gap-2">
                          <button data-testid="admin-approve-btn" onClick={() => reviewSuggestion(s.id, "godkjent")}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded-full px-4 py-2 transition-all hover:-translate-y-0.5">
                            <CheckCircle2 size={12} /> Godkjenn
                          </button>
                          <button data-testid="admin-reject-btn" onClick={() => reviewSuggestion(s.id, "avvist")}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-destructive border border-border rounded-full px-4 py-2 transition-colors">
                            Avvis
                          </button>
                        </span>
                      )}
                      {s.status === "godkjent" && (
                        <button data-testid="admin-unpublish-btn" onClick={() => reviewSuggestion(s.id, "avvist")}
                          className="ml-auto text-xs font-semibold text-muted-foreground hover:text-destructive transition-colors">
                          Fjern fra katalogen
                        </button>
                      )}
                    </div>
                    <h3 className="mt-3 text-base font-semibold">{s.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.description}</p>
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs font-mono text-primary hover:underline">{s.url}</a>
                  </article>
                ))}
                {suggestions.length === 0 && (
                  <p data-testid="admin-suggestions-empty" className="text-sm text-muted-foreground py-10 text-center">
                    Ingen forslag fra NAV ennå.
                  </p>
                )}
              </div>
            )}

            {tab === "statistikk" && stats && (
              <div className="mt-6" data-testid="admin-stats-section">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { label: "Sidevisninger", value: stats.pageviews },
                    { label: "AI-spørsmål stilt", value: stats.ai_questions },
                    { label: "Klikk til offentlige sider", value: stats.clicks },
                  ].map((c) => (
                    <div key={c.label} className="bg-surface border border-border rounded-2xl p-6">
                      <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">{c.label}</p>
                      <p className="mt-2 font-serif font-bold text-4xl">{c.value}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-surface border border-border rounded-2xl p-6">
                    <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">Sidevisninger siste 14 dager</p>
                    <div className="mt-5 flex items-end gap-1.5 h-28">
                      {stats.days.slice(-14).map((d) => (
                        <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5" title={`${d.date}: ${d.count}`}>
                          <div className="w-full bg-primary/80 rounded-t-md" style={{ height: `${Math.max((d.count / maxDay) * 100, 3)}%` }} />
                        </div>
                      ))}
                      {stats.days.length === 0 && <p className="text-sm text-muted-foreground">Ingen data ennå.</p>}
                    </div>
                  </div>
                  <div className="bg-surface border border-border rounded-2xl p-6">
                    <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">Mest klikkede ressurser</p>
                    <ul className="mt-5 space-y-3">
                      {stats.top_resources.map((r) => (
                        <li key={r.id} className="flex items-center justify-between gap-3 text-sm">
                          <span className="truncate">{r.title}</span>
                          <span className="font-mono text-xs font-semibold text-muted-foreground shrink-0">{r.clicks} klikk</span>
                        </li>
                      ))}
                      {stats.top_resources.length === 0 && <p className="text-sm text-muted-foreground">Ingen klikk registrert ennå.</p>}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {tab === "henvendelser" && (
              <>
                <p data-testid="admin-inquiry-count" className="mt-6 text-xs font-mono text-muted-foreground">
                  {newCount > 0 ? `${newCount} nye · ` : ""}{inquiries.length} henvendelse{inquiries.length === 1 ? "" : "r"} totalt
                </p>

                <div className="mt-4 space-y-4">
                  {sorted.map((iq) => (
                    <article key={iq.id} data-testid="admin-inquiry-card"
                      className={`bg-surface border rounded-2xl p-6 transition-colors ${iq.status === "behandlet" ? "border-border opacity-70" : "border-terracotta/40"}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-[11px] font-mono font-semibold uppercase tracking-[0.15em] bg-accent text-accent-foreground px-3 py-1.5 rounded-full">
                          {iq.topic}
                        </span>
                        {iq.status === "behandlet" ? (
                          <span data-testid="admin-status-done" className="inline-flex items-center gap-1 text-[11px] font-mono font-semibold uppercase tracking-[0.15em] text-muted-foreground bg-muted px-3 py-1.5 rounded-full">
                            <CheckCircle2 size={11} /> Behandlet
                          </span>
                        ) : (
                          <span data-testid="admin-status-new" className="inline-flex items-center gap-1 text-[11px] font-mono font-semibold uppercase tracking-[0.15em] text-terracotta bg-terracotta/10 px-3 py-1.5 rounded-full">
                            Ny
                          </span>
                        )}
                        <span className="text-xs font-mono text-muted-foreground">{fmtDate(iq.created_at)}</span>
                        <button
                          data-testid="admin-toggle-status"
                          onClick={() => toggleStatus(iq)}
                          className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground border border-border hover:border-foreground/30 rounded-full px-3 py-1.5 transition-colors"
                        >
                          {iq.status === "behandlet" ? <><Undo2 size={12} /> Marker som ny</> : <><CheckCircle2 size={12} /> Merk som behandlet</>}
                        </button>
                      </div>
                      <p className="mt-4 text-sm leading-relaxed whitespace-pre-wrap">{iq.message}</p>
                      {iq.email && (
                        <a data-testid="admin-inquiry-email" href={`mailto:${iq.email}`}
                          className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
                          <Mail size={12} /> {iq.email}
                        </a>
                      )}
                    </article>
                  ))}
                  {inquiries.length === 0 && (
                    <p data-testid="admin-empty-state" className="text-sm text-muted-foreground py-10 text-center">
                      Ingen henvendelser ennå.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
